import React from 'react';
import classes from './Checkout.module.css';
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { Text, NumberInput, Loader, Divider, Checkbox, Anchor, Badge, Group, Stack, Select } from '@mantine/core';
import { IconCheck, IconClock, IconApps } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../Cart/CartContext';
import { OrderData, LoadOrderDataAsync } from '../OrderList/OrderList';
import { ListReleasedAppNames, allFeatures, runningPackageEnd } from '../../scripts/FeatureCheck';
import { PAYPAL_ENV } from '../../config/paypal';
import { useTranslation } from "react-i18next";
import { DEBUG } from '../..';

interface CheckoutProviderProps {
  logged_in: boolean;
  isPending: boolean;
  cartProduct: 'lifetime' | 'welcome';
  onApproveNavigation: () => void;
  t: (key: string) => string;
}

export type Product = {
  name: string;
  value: number;
  tax: number;
  discount: Discount;
}

export type Discount = {
  discount: number;
  start: Date;
  end: Date;
  affiliate: string;
}

const HolidayDiscount: Discount = {
  discount: 5.00,
  start: new Date(2024, 11, 19, 0, 0, 0),
  end: new Date(2024, 11, 31, 23, 59, 59),
  affiliate: ""
}

const ACEDRIVE: Product = {
  name: "ACE DRIVE",
  value: 25.00,
  tax: 3.99,
  discount: HolidayDiscount
}

const WELCOME_PACKAGE: Product = {
  name: "ACE DRIVE Welcome Package",
  value: 20.00,
  tax: 3.19,
  discount: { discount: 0, start: new Date(0), end: new Date(0), affiliate: "" }
}

const initialState = {
  quantity: 1,
  product: ACEDRIVE,
  orderID: "",
  onApproveMessage: "",
  onErrorMessage: "",
  logged_in: false,
  isPending: false,
  tosAccepted: false,
  // Keys the Welcome Package can be booked onto.
  packageKeys: [] as OrderData[],
  selectedPackageKey: ''
};
export class CheckoutProvider extends React.Component<CheckoutProviderProps, typeof initialState> {

  activeDiscount = 0.00;
  constructor(props: CheckoutProviderProps) {
    super(props);
    const product = props.cartProduct === 'welcome' ? WELCOME_PACKAGE : ACEDRIVE;
    this.state = {
      ...initialState,
      product,
      logged_in: props.logged_in,
      isPending: props.isPending
    };

    this.onChange = this.onChange.bind(this);
    this.createOrder = this.createOrder.bind(this);
    this.onApprove = this.onApprove.bind(this);
    this.onError = this.onError.bind(this);
    this.onClick = this.onClick.bind(this);
    var now = new Date();
    this.activeDiscount = now >= this.state.product.discount.start && now <= this.state.product.discount.end ? this.state.product.discount.discount : 0.00
    console.log(`now: ${now}`);
    console.log(`discount: ${this.state.product.discount}`);
    console.log(`start: ${this.state.product.discount.start}`);
    console.log(`end: ${this.state.product.discount.end}`);
    console.log(`after start: ${(now >= this.state.product.discount.start)}`);
    console.log(`after start: ${(now <= this.state.product.discount.end)}`);
  }
  async componentDidMount() {
    // Not logged in, get-orders.php answers with a JSON string rather than a list.
    const owned = await LoadOrderDataAsync();
    if (!Array.isArray(owned)) return;
    // A refunded key is dead weight in production, but on the sandbox build every test
    // purchase creates one, so there would be nothing to pick otherwise.
    this.setState({ packageKeys: owned.filter(k => PAYPAL_ENV === 'sandbox' || !k.refunded) });
  }

  packageKeyLabel = (key: OrderData): string => {
    const { t } = this.props;
    const apps = ListReleasedAppNames(allFeatures(key)).join(', ') || t('cart.welcome.targetKeyNew');
    const running = runningPackageEnd(key);
    const suffix = running
      ? ` · ${t('cart.welcome.targetKeyRunsUntil')} ${running.toLocaleDateString('de-DE')}`
      : '';
    return `${key.ident.slice(0, 8)}… — ${apps}${suffix}${key.refunded ? ' · refunded' : ''}`;
  }

  componentDidUpdate(prevProps: CheckoutProviderProps) {
    if (prevProps.isPending !== this.props.isPending) {
      this.setState({ isPending: this.props.isPending });
    }
    if (prevProps.cartProduct !== this.props.cartProduct) {
      const product = this.props.cartProduct === 'welcome' ? WELCOME_PACKAGE : ACEDRIVE;
      const now = new Date();
      this.activeDiscount = now >= product.discount.start && now <= product.discount.end ? product.discount.discount : 0.00;
      this.setState({ product, quantity: 1 });
    }
  }
  onChange(value: any) {
    if(value == "") value = 1;
    this.setState({
      quantity: value,
      orderID: "",
      onApproveMessage: "",
      onErrorMessage: ""
    });
  }

  createOrder(data: Record<string, unknown>, actions: any) {
    const ref = localStorage.getItem("ref") ?? "";
    return actions.order
      .create({
        purchase_units: [
          {
            amount: {
              value: (this.state.quantity * (this.state.product.value - this.activeDiscount)).toFixed(2),
              breakdown: {
                item_total: {
                  value: (this.state.quantity * (this.state.product.value - this.activeDiscount)).toFixed(2),
                  currency_code: "EUR"
                }
              }
            },
            items: [
              {
                name: this.state.product.name,
                quantity: this.state.quantity,
                unit_amount: {
                  value: (this.state.product.value - this.activeDiscount),
                  currency_code: "EUR"
                },
                image_url: "https://accdrive.com/static/media/drive-logo.1a151a4c09722e32d368.png",
                url: "https://accdrive.com/"
              }
            ]
          }
        ]
      })
      .then((orderID: any) => {
        this.setState({ orderID: orderID });
        this.logOrder(ref, orderID)
        return orderID;
      });
  }

  async logOrder(ref: string | null, id: string) {
    const formData = new FormData();
    formData.append('amount', this.state.quantity.toString());
    formData.append('affiliate', ref ?? "");
    formData.append('id', id);
    formData.append('st', 'CREATED');
    formData.append('productType', this.props.cartProduct ?? 'lifetime');

    try {
      const response = await fetch('/dyn/process-order.php?mode=create', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const result = await response.json();
    } catch (error: any) {
      if(DEBUG) console.error("Order Error", error.toString());
    }
  };

  async handleSubmit(ref: string | null, givenName: string, surName: string, email: string, id: string, status: string) {
    const formData = new FormData();
    formData.append('amount', this.state.quantity.toString());
    formData.append('affiliate', ref ?? "");
    formData.append('em', email);
    formData.append('id', id);
    formData.append('st', status);
    formData.append('productType', this.props.cartProduct ?? 'lifetime');
    // Empty = issue a new key. The server re-checks that this Steam account owns the
    // key before booking anything onto it.
    formData.append('pk', this.state.selectedPackageKey);

    try {
      const response = await fetch('/dyn/process-order.php?mode=confirm', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const result = await response.json();
    } catch (error: any) {
      if(DEBUG) console.error("Order Error", error.toString());
    }
  };

  async handleSubmitRetry(ref: string | null, givenName: string, surName: string, email: string, id: string, status: string) {
    const formData = new FormData();
    formData.append('amount', this.state.quantity.toString());
    formData.append('affiliate', ref ?? "");
    formData.append('gn', givenName);
    formData.append('sn', surName);
    formData.append('em', email);
    formData.append('id', id);
    formData.append('st', status);
  
    let attempt = 0;
    const maxAttempts = 10;
  
    while (attempt < maxAttempts) {
      try {
        const response = await fetch('/dyn/process-order.php', {
          method: 'POST',
          body: formData,
        });
  
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
  
        const result = await response.json();
        
        if (result[0]?.State === 'ERROR') {
          attempt++;
          if (attempt >= maxAttempts) {
            throw new Error(result[0]?.Error || 'Max attempts reached with errors.');
          }
        } else {
          // Erfolgreiche Antwort behandeln
          console.log('Order successfully processed:', result);
          break;
        }
      } catch (error: any) {
        if (DEBUG) console.error("Order Error", error.toString());
        attempt++;
        if (attempt >= maxAttempts) {
          console.error('Max attempts reached. Final error:', error.toString());
          break;
        }
      }
    }
  }
  
  onApprove(data: any, actions: any) {
    let app = this;
    if(DEBUG) console.log("Capture", actions.order.capture());
    if(DEBUG) console.log("Capture Payer", actions.order);
    if(DEBUG) console.log("Get", actions.order.get());
    return actions.order.capture().then(async function (details: any) {
      const ref = localStorage.getItem("ref") ?? "";
      if(DEBUG) console.log(ref);
      await app.handleSubmit(ref, details.payer.name.given_name, details.payer.name.surname, details.payer.email_address, details.id, details.status);
      app.setState({
        onApproveMessage: `Transaction completed by ${details.payer.name.given_name}!`
      });
      
      app.props.onApproveNavigation();
    });
  }

  onError(err: Record<string, unknown>) {
    this.setState({
      onErrorMessage: err.toString()
    });
  }

  onClick() {
    if(DEBUG) console.log("When clicked, amount was", this.state.quantity);
  }

  render() {
    const { t } = this.props;
    return (
      this.state.logged_in ? (
        <div className={classes.styledPaypal}>
          {this.state.isPending ? <Loader color="var(--mantine-color-green-filled)" size="xl" /> : (
            <>
              <div className={classes.amountSelection}>
                <Text className={classes.amountLabel}>
                  {this.state.product.name} {t('cart.amount')}
                </Text>
                <NumberInput
                  className={classes.styledInput}
                  styles={{
                    root: { width: '100%', maxWidth: '100px', justifySelf: 'end' },
                    input: { width: '100%'}
                  }}
                  min={1}
                  max={50}
                  stepHoldDelay={500}
                  stepHoldInterval={100}
                  allowDecimal={false}
                  allowNegative={false}
                  value={this.state.quantity}
                  onChange={this.onChange}
                  clampBehavior="strict"
                />
              </div>
              <Divider my={"sm"} variant={"dashed"}/>
              {this.props.cartProduct === 'welcome' && (() => {
                const selected = this.state.packageKeys.find(k => k.ident === this.state.selectedPackageKey);
                // Buying onto a key whose package is still running appends the 90 days
                // instead of starting a second, overlapping one — same arithmetic the
                // server does.
                const runningEnd = selected ? runningPackageEnd(selected) : null;
                const validUntil = runningEnd ? new Date(runningEnd) : new Date();
                validUntil.setDate(validUntil.getDate() + 90);
                const validUntilStr = validUntil.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
                return (
                  <>
                    <Stack gap={10} mb="sm">
                      <Group gap={6}>
                        <Badge color="orange" variant="filled" size="sm" leftSection={<IconClock size={11} />}>
                          {t('cart.welcome.tagline')}
                        </Badge>
                        <Badge variant="outline" size="sm" style={{ color: 'var(--checkout-fg)', borderColor: 'var(--checkout-border)' }}>
                          {t('cart.welcome.noSub')}
                        </Badge>
                      </Group>
                      <Group gap={6} align="center">
                        <IconApps size={13} color="var(--checkout-accent)" />
                        <Text size="sm" fw={600} c="var(--checkout-fg)">
                          {t('cart.welcome.validUntilLabel')}: <span style={{ color: 'var(--checkout-accent)' }}>{validUntilStr}</span>
                        </Text>
                      </Group>
                      {this.state.packageKeys.length > 0 && (
                        <div>
                          <Text size="xs" fw={700} tt="uppercase" c="var(--checkout-fg-dim)" mb={5}>
                            {t('cart.welcome.targetKeyLabel')}
                          </Text>
                          <Select
                            size="xs"
                            allowDeselect={false}
                            value={this.state.selectedPackageKey}
                            onChange={(value) => this.setState({ selectedPackageKey: value ?? '' })}
                            data={[
                              { value: '', label: t('cart.welcome.targetKeyNew') },
                              ...this.state.packageKeys.map(k => ({ value: k.ident, label: this.packageKeyLabel(k) }))
                            ]}
                          />
                          <Text size="xs" c="var(--checkout-fg-dim)" mt={4}>{t('cart.welcome.targetKeyHint')}</Text>
                        </div>
                      )}
                      <div>
                        <Text size="xs" fw={700} tt="uppercase" c="var(--checkout-fg-dim)" mb={5}>
                          {t('cart.welcome.appsIncluded')}
                        </Text>
                        <Group gap={4}>
                          {['ACC DRIVE', 'ACC Academy', 'iR DRIVE', 'ACE DRIVE', 'AC DRIVE', 'LMU DRIVE'].map(app => (
                            <Badge key={app} variant="outline" size="xs" radius="sm" style={{ color: 'var(--checkout-fg)', borderColor: 'var(--checkout-border)' }}>{app}</Badge>
                          ))}
                        </Group>
                      </div>
                      <Stack gap={5}>
                        {(['cart.welcome.benefit1', 'cart.welcome.benefit2', 'cart.welcome.benefit3'] as const).map(key => (
                          <Group key={key} gap={6} align="flex-start">
                            <IconCheck size={13} color="var(--checkout-ok)" style={{ marginTop: 2, flexShrink: 0 }} />
                            <Text size="xs" c="var(--checkout-fg)">{t(key)}</Text>
                          </Group>
                        ))}
                      </Stack>
                    </Stack>
                    <Divider my="sm" color="orange.3" variant="dashed" />
                  </>
                );
              })()}
              <div className={classes.cartSumSelection}>
                <Text className={classes.amountLabel}>
                  {t('cart.sum')}
                </Text>
                {(this.activeDiscount > 0.00 ?
                  <div className={classes.cartSumDiscountGroup}>
                    <Text className={`${classes.cartSumDiscounted} ${classes.strikethrough}`}>
                      {(this.state.product.value * this.state.quantity).toFixed(2)}€
                    </Text>
                    <Text className={classes.cartSum}>
                      {((this.state.product.value - this.activeDiscount) * this.state.quantity).toFixed(2)}€
                    </Text>
                  </div> :
                  <>
                    <Text className={classes.cartSum}>
                      {(this.state.product.value * this.state.quantity).toFixed(2)}€
                    </Text>
                  </>)}
              </div>
              <Divider my={"sm"} />
              <Checkbox
                mb={"sm"}
                checked={this.state.tosAccepted}
                onChange={() => this.setState({ tosAccepted: !this.state.tosAccepted })}
                label={
                  <Text size="xs">
                    {t('cart.tosCheckbox.prefix')}{' '}
                    <Anchor href="/tos" target="_blank" size="xs">
                      {t('cart.tosCheckbox.tos')}
                    </Anchor>
                    {' '}{t('cart.tosCheckbox.and')}{' '}
                    <Anchor href="/privacy_web" target="_blank" size="xs">
                      {t('cart.tosCheckbox.privacy')}
                    </Anchor>
                  </Text>
                }
              />
              <div style={{ position: 'relative' }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", shape: "rect" }}
                  createOrder={this.createOrder}
                  onApprove={this.onApprove}
                />
                {!this.state.tosAccepted && (
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backdropFilter: 'blur(3px)',
                    backgroundColor: 'rgba(0,0,0,0.15)',
                    borderRadius: '4px',
                    cursor: 'not-allowed',
                    zIndex: 9999
                  }} />
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <></>
      )
    );
  }
}
export const Checkout = (logged_in: any) => {
  const { closeCart, cartProduct } = useCart();
  const navigate = useNavigate();
  const { t } = useTranslation("translation");
  const redirectToProfile = () => {
    closeCart();
    navigate("/account");
  };
  const [{ options, isPending }, dispatch] = usePayPalScriptReducer();
  return (
    <CheckoutProvider logged_in={logged_in} isPending={isPending} cartProduct={cartProduct ?? 'lifetime'} onApproveNavigation={redirectToProfile} t={t} />
  )
}